/*
 * Copyright (c) Airbus SAS. All rights reserved.
 */

// CSS
import '@/scss/app.scss'

// JS
import '@/stimulus_bootstrap'
import $ from 'jquery'
import 'bootstrap'
import 'select2/dist/js/select2.full.min'
import { tableSort } from '@airbus/mms/js/listeners/table-sort'
import Swal from 'sweetalert2/src/sweetalert2'
// Per-element listeners (connected on every turbo:load / turbo:frame-load / stream insert)
import autoOpenModal from '@/js/listeners/auto-open-modal'
import autocomplete from '@/js/listeners/autocomplete'
import bannerCarousel from '@/js/listeners/banner-carousel'
import checkboxToggle from '@/js/listeners/checkbox-toggle'
import copyToClipboard from '@/js/listeners/copy-to-clipboard'
import file from '@/js/listeners/file'
import formCollection from '@/js/listeners/form-collection'
import formLoader from '@/js/listeners/form-loader'
import formRequiredFields from '@/js/listeners/form-required-fields'
import formToggle from '@/js/listeners/form-toggle'
import generatePassword from '@/js/listeners/generate-password'
import headerMenu from '@/js/listeners/header-menu'
import imageFallback from '@/js/listeners/image-fallback'
import inputFile from '@/js/listeners/input-file'
import markdownEditor from '@/js/listeners/markdown-editor'
import modalTemplate from '@/js/listeners/modal-template'
import pages from '@/js/listeners/pages'
import passwordToggle from '@/js/listeners/password-toggle'
import popover from '@/js/listeners/popover'
import search from '@/js/listeners/search'
import select from '@/js/listeners/select'
import switchUser from '@/js/listeners/switch-user'
import toggleCard from '@/js/listeners/toggle-card'
import tooltip from '@/js/listeners/tooltip'
// One-time modules (run once at boot, bound to document/window)
import framePoll from '@/js/modules/frame-poll'
import menu from '@/js/modules/menu'
import relogin from '@/js/modules/relogin'
import sentry from '@/js/modules/sentry'
import turbo from '@/js/modules/turbo'

import registerServices from '@/js/services'
import Container from '@/js/services/Container'
import { domReady, findAllSelf } from '@/js/utils/dom'

global.$ = global.jQuery = window.$ = window.jQuery = $
global.Swal = Swal

/**
 * Application entry point.
 *
 * Owns the DI container, the listener registry, and the mount/unmount
 * lifecycle. Modules run once at boot; listeners are `{selector, connect}`
 * pairs connected per element by `mount(container)` and torn down by
 * `unmount(container)`.
 *
 * Bookkeeping is PER ELEMENT, not per container: `mount` skips elements that
 * are already connected (so overlapping containers — body, a nested frame, a
 * stream-inserted node — can't double-init), and `unmount(container)` tears
 * down every connected element the container covers, no matter which mount
 * call connected it (so a Drive visit's `unmount(body)` also reaches elements
 * that were connected by a frame render or a stream insert).
 *
 * See `types.js` for `Listener`, `Module`, `Page`, `Cleanup`.
 */
class App {
    #di = null

    /** @type {Module[]} */
    #modules = []

    /** @type {Listener[]} */
    #listeners = []

    /** @type {Map<Listener, Set<Element>>} */
    #tracked = new Map()

    /** @type {WeakMap<Element, Map<Listener, Cleanup>>} */
    #cleanups = new WeakMap()

    /** @type {Object<string, Page>} */
    #pages = {}

    /** @type {Object<string, Array<() => void>>} */
    #pageResolvers = {}

    constructor() {
        this.#modules = [turbo, sentry, relogin, menu, framePoll]

        this.#listeners = [
            pages,
            headerMenu,
            switchUser,
            autocomplete,
            bannerCarousel,
            copyToClipboard,
            formCollection,
            formLoader,
            formToggle,
            formRequiredFields,
            file,
            inputFile,
            search,
            select,
            tableSort,
            toggleCard,
            tooltip,
            popover,
            passwordToggle,
            markdownEditor,
            checkboxToggle,
            generatePassword,
            imageFallback,
            modalTemplate,
            // After modal-template: its `show.bs.modal` handler must be attached before we click.
            autoOpenModal,
        ]

        this.#listeners.forEach((listener) => this.#tracked.set(listener, new Set()))
    }

    /**
     * Boot the app. Initializes the DI container and runs every module
     * exactly once.
     *
     * @param {Object} parameters - Bootstrap params (DSN, release, user, …)
     */
    start(parameters) {
        this.#di = new Container(parameters)
        registerServices(this.#di, this)

        this.#modules.forEach((module) => module({ app: this }))
    }

    /**
     * Connect every listener to its matching elements within `container`.
     * Elements already connected are skipped, so overlapping mounts are safe.
     *
     * @param {Element | Document} container
     */
    mount(container) {
        this.#listeners.forEach((listener) => {
            const tracked = this.#tracked.get(listener)

            findAllSelf(listener.selector, container).forEach((element) => {
                if (tracked.has(element)) {
                    return
                }
                tracked.add(element)

                // Fire-and-forget: mount() stays synchronous/non-blocking while
                // #connect awaits a possibly-async connect and registers its cleanup.
                this.#connect(listener, element)
            })
        })
    }

    /**
     * Disconnect every connected element that `container` covers: the
     * container itself, its descendants, and — as a safety net — any element
     * that already left the DOM (removed by JS without an unmount call).
     *
     * @param {Element | Document} container
     */
    unmount(container) {
        this.#listeners.forEach((listener) => {
            this.#tracked.get(listener).forEach((element) => {
                if (element.isConnected && element !== container && !container.contains(element)) {
                    return
                }
                this.#disconnect(listener, element)
            })
        })
    }

    /**
     * Connect one element and register its cleanup. `connect` may be sync or
     * async — awaiting resolves both uniformly. Called fire-and-forget from
     * mount(), so mount() never blocks on a listener's async setup.
     *
     * @param {Listener} listener
     * @param {Element} element
     */
    async #connect(listener, element) {
        try {
            const cleanup = await listener.connect(element, { app: this })
            this.#registerCleanup(listener, element, cleanup)
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * Store a listener's `cleanup` for `element` — or, when the element was
     * already unmounted while an async `connect` was still pending, run the
     * cleanup immediately so nothing leaks. A non-function result (a listener
     * that returns nothing) is ignored. Centralizes the unmount-during-pending-
     * connect race that each async listener would otherwise have to guard.
     *
     * @param {Listener} listener
     * @param {Element} element
     * @param {Cleanup|void} cleanup
     */
    #registerCleanup(listener, element, cleanup) {
        if (typeof cleanup !== 'function') {
            return
        }

        if (!this.#tracked.get(listener).has(element)) {
            try {
                cleanup()
            } catch (error) {
                console.error(error)
            }

            return
        }

        this.#cleanupsFor(element).set(listener, cleanup)
    }

    /**
     * @param {Listener} listener
     * @param {Element} element
     */
    #disconnect(listener, element) {
        this.#tracked.get(listener).delete(element)

        const cleanups = this.#cleanups.get(element)
        const cleanup = cleanups?.get(listener)
        if (!cleanup) {
            return
        }

        cleanups.delete(listener)
        try {
            cleanup()
        } catch (error) {
            console.error(error)
        }
    }

    /**
     * @param {Element} element
     * @returns {Map<Listener, Cleanup>}
     */
    #cleanupsFor(element) {
        let cleanups = this.#cleanups.get(element)
        if (!cleanups) {
            cleanups = new Map()
            this.#cleanups.set(element, cleanups)
        }

        return cleanups
    }

    /**
     * Register a page initializer keyed by id. Resolves any pending
     * `loadPage(id, …)` calls that arrived before the initializer was known.
     *
     * @param {string} pageId
     * @param {Page} initializer
     */
    registerPage(pageId, initializer) {
        if (this.#pages[pageId]) {
            console.warn(`Page initializer for ${pageId} already exists.`)
            return
        }

        this.#pages[pageId] = initializer

        const resolvers = this.#pageResolvers[pageId]
        if (resolvers) {
            delete this.#pageResolvers[pageId]
            resolvers.forEach((resolve) => resolve())
        }
    }

    /**
     * Invoke a registered page initializer. If the page hasn't been registered
     * yet (lazy webpack chunk), the call is queued and replayed when
     * `registerPage` lands.
     *
     * Note: `params` must not contain `app` or `container` keys — those are
     * framework-reserved.
     *
     * @param {string} pageId
     * @param {Object} params
     * @param {Element} container
     * @returns {Promise<Cleanup | void>}
     */
    async loadPage(pageId, params, container) {
        if (this.#pages[pageId]) {
            return this.#pages[pageId]({ app: this, container, ...params })
        }

        return new Promise((resolve) => {
            ;(this.#pageResolvers[pageId] ??= []).push(() => resolve(this.loadPage(pageId, params, container)))
        })
    }

    /**
     * @param {string} url
     */
    loadUrl(url) {
        Turbo.visit(url)
    }

    /**
     * Resolve a service from the DI container.
     *
     * @param {string} key
     * @returns {*}
     */
    get(key) {
        return this.#di.get(key)
    }

    /**
     * @returns {boolean}
     */
    isDev() {
        return this.get('environment') === 'dev'
    }
}

window.App = new App()
domReady().then(() => window.App.start(window.appParams))
